import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { useDevMode } from "@/context/DevModeContext";
import { FREE_WEEKLY_LIMIT, useResetSubscription, useSetAvailableActions } from "@/lib/plan";

/**
 * Hidden-in-plain-sight dev tools. A tiny, low-opacity dot pinned to the
 * top-left corner of every page; clicking it opens the Dev options panel.
 * Easy to miss unless you know it's there. The master switch is "Dev mode" —
 * when on, dev-only affordances appear across the app (sample-test seeding on
 * the dashboard, the onboarding toggles on Build Blueprint, the failed-
 * generation sample loader, "Reset subscription" for retesting the payment flow,
 * and "Set available AI actions" for the out-of-actions state, both when the
 * server enables dev tools). The dot turns amber while dev mode
 * is active.
 */
export function DevOptions() {
  const { devMode, setDevMode } = useDevMode();
  const { user } = useAuth();
  // Runtime config, so it also works in the deployed dev cluster. Hiding the
  // control is a convenience; waggle-api enforces its own flag and test-key check.
  const { data: config } = useQuery<{ devTools?: boolean }>({ queryKey: ["/api/config"], staleTime: Infinity });
  const showBillingTools = devMode && !!user && !!config?.devTools;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Dev options"
          title="Dev options"
          className={`fixed top-2 left-2 z-100 h-3 w-3 rounded-full transition-colors ${
            devMode
              ? "bg-amber-400 hover:bg-amber-500"
              : "bg-muted-foreground/20 hover:bg-muted-foreground/80"
          }`}
          data-testid="button-dev-options"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-64"
        data-testid="popover-dev-options"
      >
        <div className="space-y-3">
          <p className="text-sm font-semibold">Dev options</p>

          <button
            type="button"
            onClick={() => setDevMode(!devMode)}
            className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-muted transition-colors"
            data-testid="toggle-dev-mode"
          >
            <span>Dev mode</span>
            <span
              className={`text-xs font-semibold ${devMode ? "text-amber-600" : "text-muted-foreground"}`}
            >
              {devMode ? "ON" : "OFF"}
            </span>
          </button>

          <p className="text-xs text-muted-foreground">
            {devMode
              ? "Dev affordances are showing across the app."
              : "Turn on to reveal sample-test seeding, onboarding toggles, and the failed-generation sample loader."}
          </p>

          {showBillingTools && <BillingDevTools />}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function BillingDevTools() {
  const reset = useResetSubscription();
  const setActions = useSetAvailableActions();
  const [remaining, setRemaining] = useState("0");
  const remainingNum = Number(remaining);
  const remainingValid = remaining.trim() !== "" && Number.isInteger(remainingNum) && remainingNum >= 0 && remainingNum <= FREE_WEEKLY_LIMIT;
  const [resetUsage, setResetUsage] = useState(true);

  const errorMessage = reset.error
    ? reset.error.message.startsWith("404")
      ? "Dev tools are disabled on this server"
      : reset.error.message
    : null;

  return (
    <div className="space-y-2 border-t pt-3" data-testid="dev-billing-tools">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Billing</p>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={resetUsage}
          onCheckedChange={(v) => setResetUsage(v === true)}
          data-testid="dev-reset-usage"
        />
        Also reset usage
      </label>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        disabled={reset.isPending}
        onClick={() => reset.mutate({ resetUsage })}
        data-testid="dev-reset-subscription"
      >
        {reset.isPending ? "Resetting…" : "Reset subscription"}
      </Button>
      {reset.isSuccess && (
        <p className="text-xs text-muted-foreground" data-testid="dev-reset-success">
          Reset to Free
        </p>
      )}
      {errorMessage && (
        <p className="text-xs text-destructive" data-testid="dev-reset-error">
          {errorMessage}
        </p>
      )}

      <div className="space-y-2 border-t pt-3">
        <label htmlFor="dev-available-actions" className="text-sm">
          Available AI actions this week (0–{FREE_WEEKLY_LIMIT})
        </label>
        <p className="text-xs text-muted-foreground">
          At 0, generate, regenerate and add show the upgrade prompt.
        </p>
        <div className="flex gap-2">
          <Input
            id="dev-available-actions"
            type="number"
            min={0}
            max={FREE_WEEKLY_LIMIT}
            value={remaining}
            onChange={(e) => setRemaining(e.target.value)}
            className="h-8 w-16"
            data-testid="dev-available-actions"
          />
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            disabled={!remainingValid || setActions.isPending}
            onClick={() => setActions.mutate(remainingNum)}
            data-testid="dev-set-available-actions"
          >
            {setActions.isPending ? "Setting…" : "Set available actions"}
          </Button>
        </div>
        {setActions.isSuccess && (
          <p className="text-xs text-muted-foreground" data-testid="dev-set-actions-success">
            {remainingNum === 0 ? "No actions left" : `${remainingNum} left`} this week
          </p>
        )}
        {setActions.error && (
          <p className="text-xs text-destructive" data-testid="dev-set-actions-error">
            {setActions.error.message.startsWith("404") ? "Dev tools are disabled on this server" : setActions.error.message}
          </p>
        )}
      </div>
    </div>
  );
}
